import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { Property } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PropertyViewModalProps {
  isOpen: boolean;
  property: Property | null;
  onClose: () => void;
  onEdit?: (property: Property) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (propertyId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? String(value) : '-'}
    </div>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const PropertyViewModal: React.FC<PropertyViewModalProps> = ({
  isOpen,
  property,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !property) return;
    minimize({
      id: `${tabId}-view-modal-${property.id}`,
      title: `View - ${property.propertyCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(property.id),
    });
    onClose();
  }, [tabId, moduleName, property, minimize, onClose, onRestoreView]);

  if (!isOpen || !property) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Property Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Basic Information */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Property Code" value={property.propertyCode} />
            <ReadOnlyField label="Property Name" value={property.propertyName} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Property Type" value={property.propertyType} />
            <ReadOnlyField label="Star Rating" value={property.starRating} />
            <ReadOnlyField label="Total Rooms" value={property.totalRooms} />
          </div>

          {/* Address */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Address Line 1" value={property.addressLine1} />
            {property.addressLine2 && <ReadOnlyField label="Address Line 2" value={property.addressLine2} />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="City" value={property.city} />
            <ReadOnlyField label="State" value={property.state} />
            <ReadOnlyField label="Country" value={property.country} />
            <ReadOnlyField label="Pincode" value={property.pincode} />
          </div>

          {/* Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Phone (Primary)" value={property.phonePrimary} />
            <ReadOnlyField label="Phone (Secondary)" value={property.phoneSecondary} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Email" value={property.email} />
            <ReadOnlyField label="Website" value={property.website} />
          </div>

          {/* Tax / Legal */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="GST Number" value={property.gstNumber} />
            <ReadOnlyField label="Tax State Code" value={property.taxStateCode} />
            <ReadOnlyField label="PAN Number" value={property.panNumber} />
          </div>

          {/* Operations */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Check-in Time" value={property.checkInTime} />
            <ReadOnlyField label="Check-out Time" value={property.checkOutTime} />
            <ReadOnlyField label="Currency" value={property.currencyCode} />
            <ReadOnlyField label="Timezone" value={property.timezone} />
          </div>

          {/* Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${property.isActive ? 'active' : 'inactive'}`}>
                  {property.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(property);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PropertyViewModal;
